import {
  ErrorBoundary,
  SearchBox,
  SearchProvider,
  WithSearch,
} from '@elastic/react-search-ui';
import { Layout } from '@elastic/react-search-ui-views';
import React, { LegacyRef } from 'react';

import { ReactComponent as Close } from '/src/images/icons/close.svg';
import { ReactComponent as SearchIcon } from '/src/images/icons/search.svg';

import { isBrowser } from '../../helpers/is-browser';
import { calculatePrefixPath } from '../../helpers/rendered-page-creation/calculate-prefixed-path';
import { IContentfulImage } from '../../interfaces/contentful-image.interface';
import { ILibraryMeetingPlace } from '../../queries/fragments/libraryMeetingPlace';
import { IMeetingPlacePageSearchResults } from '../../queries/fragments/meetingPlacePageSearchResults';
import ImageWrapper from '../image-wrapper/Image-wrapper';
import { searchDriver } from './search-driver';
import * as styles from './search-widget.module.scss';

interface SearchWidgetProps {
  searchClass: string;
  toggleSearch: () => void;
  logo: IContentfulImage;
  node_locale: string;
  meetingPlace: ILibraryMeetingPlace;
  el: LegacyRef<HTMLDivElement> | undefined;
  searchResults?: IMeetingPlacePageSearchResults;
}

export const SearchWidget = ({
  searchClass,
  toggleSearch,
  logo,
  node_locale,
  meetingPlace,
  el,
  searchResults,
}: SearchWidgetProps) => {
  const handleOnSubmitSearchForm = (searchTerm: string) => {
    const sanitizedSearchTerm = searchTerm.replace(/[^\p{L}\p{N} ]/gu, '');
    const searchResultUrl = calculatePrefixPath(
      `/${node_locale}/${searchResults?.slug}?q=${sanitizedSearchTerm}`,
    );
    toggleSearch();
    window.location.href = searchResultUrl; // If we use gatsby navigate() here the search result page will not be updated with the search results on the second search.
  };
  const aspectRatio =
    logo?.gatsbyImageData?.width && logo?.gatsbyImageData?.height
      ? logo.gatsbyImageData.width / logo.gatsbyImageData.height
      : undefined;

  return (
    <div
      ref={el}
      id="search-widget"
      className={`fixed bottom-0 left-0 z-50 -mb-5 ml-[50%] h-[100dvh] w-screen -translate-x-1/2 overflow-y-auto bg-background-primary-light pb-96 pt-7 transition-all duration-300 ease-out lg:fixed lg:bottom-auto lg:left-0 lg:right-0 lg:top-0 lg:mt-0 lg:h-auto lg:animate-fadeIn lg:rounded-b-xl lg:py-7.5 lg:shadow-default ${searchClass}`}
    >
      {isBrowser && (
        <SearchProvider config={searchDriver(node_locale)}>
          <WithSearch mapContextToProps={() => ({})}>
            {() => {
              return (
                <>
                  <div className="flex flex-col items-stretch px-6 xl:container lg:flex-row lg:p-0 lg:px-28 xl:px-28">
                    {logo && (
                      <div className="lg:mt-1.5 lg:basis-36">
                        <ImageWrapper
                          className={
                            aspectRatio == 1 ? 'max-h-15 w-36' : 'max-h-10 w-36'
                          }
                          imgClassName={
                            aspectRatio == 1
                              ? 'max-h-15 !w-auto'
                              : 'max-h-10 !w-auto'
                          }
                          alt={logo.description ? logo.description : ''}
                          file={logo.file}
                          gatsbyImageData={logo.gatsbyImageData}
                          svg={logo.svg}
                        />
                        <div className="mt-3 font-title text-headingxs text-text-default lg:hidden">
                          {meetingPlace.name}
                        </div>
                      </div>
                    )}
                    <ErrorBoundary>
                      <Layout
                        className="max-lg:mt-5 lg:ml-8 lg:flex-1"
                        header={
                          <SearchBox
                            inputProps={{
                              autoFocus: true,
                            }}
                            autocompleteMinimumCharacters={2}
                            debounceLength={200}
                            autocompleteView={({
                              autocompletedResults,
                              getItemProps,
                            }) => (
                              <div className="sui-search-box__autocomplete-container rounded-b-xl border-x border-b border-neutral-grey-200 bg-brand-white">
                                {autocompletedResults.length > 0 && (
                                  <ol className="mx-6 border-t border-neutral-grey-200 pb-4 pt-3 text-text-default">
                                    {autocompletedResults.map((result, i) => (
                                      <li
                                        className={`py-2 font-title hover:text-text-light ${styles.autocompleteitem}`}
                                        key={i}
                                        {...getItemProps({
                                          index: i,
                                          key: String(i), // Convert i to a string here as well
                                          item: result,
                                        })}
                                      >
                                        {result.search_title?.raw}
                                        <span className="ml-1 font-sans text-bodys">
                                          {result.page_category?.raw}
                                        </span>
                                      </li>
                                    ))}
                                  </ol>
                                )}
                              </div>
                            )}
                            onSubmit={handleOnSubmitSearchForm}
                            inputView={({
                              getAutocomplete,
                              getInputProps,
                              getButtonProps,
                            }) => (
                              <div className="sui-search-box__wrapper relative ">
                                <input
                                  {...getInputProps({
                                    placeholder: searchResults?.placeholder
                                      ? searchResults?.placeholder
                                      : 'Missing placeholder in Contentful',
                                    type: 'search',
                                    className:
                                      'border w-full text-bodyl outline-none border border-neutral-grey-200 py-2.5 px-6 blackx rounded-xl w-full searchField',
                                  })}
                                />
                                {getAutocomplete()}
                                <button
                                  {...getButtonProps({})}
                                  className="absolute right-6 top-2.5 border-0 border-l border-neutral-grey-200 p-1.5 pl-4"
                                >
                                  <SearchIcon className="h-3 w-3 text-icon-dark" />
                                </button>
                              </div>
                            )}
                            autocompleteResults={{
                              titleField: 'search_title',
                              urlField: 'url_path',
                            }}
                          />
                        }
                      />
                    </ErrorBoundary>
                  </div>
                  <button
                    onClick={toggleSearch}
                    className="absolute right-6 top-7 grid h-14 w-14 place-items-center content-center rounded-full bg-brand-white hover:bg-neutral-grey-100 focus:bg-brand-white focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-3 focus-visible:outline-icon-dark active:bg-neutral-grey-200 lg:right-6 lg:top-6 lg:h-15 lg:w-15"
                  >
                    <Close />
                  </button>
                </>
              );
            }}
          </WithSearch>
        </SearchProvider>
      )}
    </div>
  );
};
